import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Star, StarOff, GitBranch, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const WorkflowsListCard = ({
  filteredWorkflows,
  loading,
  handleOpenDialog,
  handleToggleStatus,
  handleSetDefault,
  openDeleteDialog,
}) => {
  const { t } = useTranslation();
  return (
  <Card>
    <CardHeader>
      <CardTitle>{t('approvalComp.listTitle', { count: filteredWorkflows.length })}</CardTitle>
      <CardDescription>{t('approvalComp.listDesc')}</CardDescription>
    </CardHeader>
    <CardContent>
      {loading ? (
        <div className="text-center py-8">{t('common.loading')}</div>
      ) : filteredWorkflows.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">{t('approvalComp.empty')}</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('approvalComp.colCode')}</TableHead>
              <TableHead>{t('approvalComp.colName')}</TableHead>
              <TableHead>{t('approvalComp.colSteps')}</TableHead>
              <TableHead>{t('approvalComp.colStatus')}</TableHead>
              <TableHead>{t('approvalComp.colDefault')}</TableHead>
              <TableHead className="text-right">{t('approvalComp.colActions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredWorkflows.map((workflow) => (
              <TableRow key={workflow.id}>
                <TableCell>
                  <Badge variant="outline">{workflow.code}</Badge>
                </TableCell>
                <TableCell className="font-medium">{workflow.name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <GitBranch className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{t('approvalComp.stepsCount', { count: workflow.steps?.length || 0 })}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={workflow.is_active ? 'default' : 'secondary'}>
                    {workflow.is_active ? (
                      <CheckCircle className="mr-1 h-3 w-3" />
                    ) : (
                      <XCircle className="mr-1 h-3 w-3" />
                    )}
                    {workflow.is_active ? t('approvalComp.active') : t('approvalComp.inactive')}
                  </Badge>
                </TableCell>
                <TableCell>
                  {workflow.is_default && (
                    <Badge variant="default" className="bg-yellow-500">
                      <Star className="mr-1 h-3 w-3" fill="white" />
                      {t('approvalComp.defaultBadge')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {!workflow.is_default && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSetDefault(workflow.id)}
                        title={t('approvalComp.setDefault')}
                      >
                        <StarOff className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleStatus(workflow.id)}
                      title={workflow.is_active ? t('approvalComp.deactivate') : t('approvalComp.activate')}
                    >
                      {workflow.is_active ? (
                        <XCircle className="h-4 w-4 text-orange-500" />
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      )}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(workflow)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openDeleteDialog(workflow.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
  );
};

export default WorkflowsListCard;
